
import React from 'react';
import { CreditCard, Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  exp: string;
  isPrimary: boolean;
}

interface PaymentMethodsProps {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod: string;
  setSelectedPaymentMethod: (id: string) => void;
  handleAddPaymentMethod: () => void;
  handleMakePrimary: (methodId: string) => void;
}

const PaymentMethods = ({
  paymentMethods,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  handleAddPaymentMethod,
  handleMakePrimary
}: PaymentMethodsProps) => {
  return (
    <Card className="border border-gray-100 rounded-lg shadow-sm overflow-hidden h-full">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between h-[60px]">
        <h3 className="text-base font-medium text-gray-800">Payment Methods</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleAddPaymentMethod}
          className="flex items-center gap-1 text-sm h-8"
        >
          <Plus size={14} />
          Add New
        </Button>
      </div>
      <CardContent className="p-6">
        <p className="text-sm text-gray-500 mb-4">
          Your primary billing method is used for all recurring payments.
        </p>
        
        <RadioGroup
          value={selectedPaymentMethod}
          onValueChange={setSelectedPaymentMethod}
          className="space-y-4"
        >
          {paymentMethods.map((method) => (
            <div 
              key={method.id} 
              className={`border rounded-md p-4 transition-all ${
                selectedPaymentMethod === method.id 
                  ? 'border-blue-200 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-orange-100 text-orange-500">
                    <CreditCard className="h-4 w-4" />
                  </div>
                  <RadioGroupItem value={method.id} id={method.id} hideRadio className="sr-only" />
                  <Label htmlFor={method.id} className="cursor-pointer">
                    <div className="font-medium">{method.type}</div>
                    <div className="text-xs text-gray-500">
                      Ending in {method.last4} • Expires {method.exp}
                    </div>
                  </Label>
                  {method.isPrimary && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Primary
                    </span>
                  )}
                </div>
                
                <div className="hidden md:flex items-center gap-2 mt-0">
                  {!method.isPrimary && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs h-8"
                      onClick={() => handleMakePrimary(method.id)}
                    >
                      Make Primary
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="text-xs h-8">Edit</Button>
                  <Button variant="outline" size="sm" className="text-xs h-8">Remove</Button>
                </div>
              </div>
              
              <div className="flex gap-2 mt-3 md:hidden">
                {!method.isPrimary && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs h-8 flex-1"
                    onClick={() => handleMakePrimary(method.id)}
                  >
                    Make Primary
                  </Button>
                )}
                <Button variant="outline" size="sm" className="text-xs h-8 flex-1">Edit</Button>
                <Button variant="outline" size="sm" className="text-xs h-8 flex-1">Remove</Button>
              </div>
            </div>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );
};

export default PaymentMethods;
